<template>
	<div class="utterance-speaker" :class="{ 'is-revealed': isRevealed }">
		<!-- 沒有名字（unknown、空、連續同語者）時只留空欄，文字才不會跑掉 -->
		<template v-if="name">
			<button class="speaker-pill" :class="[`speaker-${colorClass}`, { 'has-hidden-name': hasHiddenName }]"
					data-action="reveal-speaker" type="button" @pointerenter="syncHiddenName" @click="handleRevealOnClick">
				<span ref="fullRef" class="speaker-full">{{ name }}</span>
				<span class="speaker-initial">{{ initial }}</span>
			</button>
			<span class="speaker-bubble">{{ name }}</span>
		</template>
	</div>
</template>
<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'

/**
 * 點膠囊浮出全名後多久收起
 */
const SPEAKER_REVEAL_MS = 2000

/**
 * 語者欄：寬版是全名膠囊，窄版縮成顏色圓點加首字，欄寬由 --speaker-column 決定
 */
const props = defineProps<{
	/**
	 * 語者名稱，空字串代表這一句不顯示標籤
	 */
	name: string
	/**
	 * 語者在講者清單中的序號（從 1 開始），決定色號
	 */
	colorNo: number
}>()

/** 全名文字元素，量測名字有沒有被縮掉或截斷 */
const fullRef = ref<HTMLElement | null>(null)
/** 名字是否沒有完整顯示，只有這種情況 hover 才浮出全名 */
const hasHiddenName = ref(false)
/** 是否正浮出全名 */
const isRevealed = ref(false)
/** 收起全名的計時器 */
let revealTimer: ReturnType<typeof setTimeout> | undefined

/** 色號 1 到 10，超過就回頭用 1 */
const colorClass = computed(() => props.colorNo % 10 || 10)
/** 圓點裡顯示的首字 */
const initial = computed(() => Array.from(props.name)[0] ?? '?')

/**
 * 量名字是不是沒完整顯示：窄版被縮成圓點、或寬版被截成 ...
 * @return {void}
 */
const syncHiddenName = (): void => {
	// 還沒掛載就不量
	const span = fullRef.value
	if (!span) {
		return
	}

	// 圓點態的全名是 display none，寬版被截斷時內容比框寬
	hasHiddenName.value = getComputedStyle(span).display === 'none' || span.scrollWidth > span.clientWidth + 1
}

/**
 * 點膠囊切換浮出全名，浮出後 2 秒自動收起
 * @return {void}
 */
const handleRevealOnClick = (): void => {
	// 清掉上一個收起計時
	clearTimeout(revealTimer)
	// 先量一次，沒移動滑鼠的觸控裝置也要吃得到 hover 的判斷
	syncHiddenName()
	// 切換浮出狀態
	isRevealed.value = !isRevealed.value
	// 浮出時排程收起
	if (isRevealed.value) {
		revealTimer = setTimeout(() => {
			isRevealed.value = false
		}, SPEAKER_REVEAL_MS)
	}
}

// 卸載時清掉計時器
onBeforeUnmount(() => clearTimeout(revealTimer))
</script>
<style scoped lang="scss">
// 語者欄
.utterance-speaker {
	position: relative;
	// 膠囊對齊第一行的視覺中線：行高 1.2em、膠囊 24px，差值除以二，跟著字級走
	margin-top: calc((1.2em - 24px) / 2);
	// 固定欄寬，每一句的文字才會從同一個 x 開始
	flex: 0 0 var(--speaker-column);
	// flex item 的 min-width 預設是 min-content，不放行長名字會把欄位撐爆
	min-width: 0;
	// 膠囊靠右，右緣對齊逐字稿左緣
	display: flex;
	justify-content: flex-end;
}

.speaker-pill {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	max-width: 100%;
	min-width: 0;
	min-height: 24px;
	padding: 4px 8px;
	border: none;
	border-radius: var(--radius-speaker-pill);
	font-family: inherit;
	font-size: var(--text-speaker-size);
	font-weight: var(--text-speaker-weight);
	color: #fff;
	// 名字太長不換行，截成 ...
	white-space: nowrap;
	text-align: center;
	cursor: pointer;
}

.speaker-full {
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
}

.speaker-initial {
	display: none;
}

// 點圓點浮出的全名
.speaker-bubble {
	position: absolute;
	top: 50%;
	left: calc(100% + 8px);
	transform: translateY(-50%);
	z-index: 3;
	padding: 6px 10px;
	border-radius: 8px;
	background: var(--ui-grey-darkest);
	color: #fff;
	font-size: var(--text-speaker-size);
	white-space: nowrap;
	opacity: 0;
	visibility: hidden;
	pointer-events: none;
	transition: opacity 0.15s ease;
}

.utterance-speaker.is-revealed .speaker-bubble {
	opacity: 1;
	visibility: visible;
}

// 桌機滑鼠移過去就看得到，觸控裝置不套用避免 hover 卡住；名字本來就看得到的不用再蓋一層
@media (hover: hover) and (pointer: fine) {
	.speaker-pill.has-hidden-name:hover ~ .speaker-bubble {
		opacity: 1;
		visibility: visible;
	}
}

// 窄版縮成 24px 圓點，視窗內文區窄於 440 時，改看視窗寬度而不是螢幕寬度
@container pane (max-width: 440px) {
	.speaker-pill {
		width: 24px;
		height: 24px;
		padding: 0;
		border-radius: var(--radius-speaker-pill);
	}

	.speaker-full {
		display: none;
	}

	.speaker-initial {
		display: block;
	}
}

// 原文視窗開著時版面一分為二，三個視窗一律用圓點，內容與上面那組相同，改一邊要記得改另一邊
.has-source {
	.speaker-pill {
		width: 24px;
		height: 24px;
		padding: 0;
		border-radius: var(--radius-speaker-pill);
	}

	.speaker-full {
		display: none;
	}

	.speaker-initial {
		display: block;
	}
}

// 十個色號
@for $n from 1 through 10 {
	.speaker-pill.speaker-#{$n} {
		background-color: var(--speaker#{$n});
	}
}

// TTS 藍底上彩色圓點要加白框才分得開
.pane-tts .speaker-pill {
	box-shadow: 0 0 0 2px #fff;
}

@supports (text-box-trim: trim-both) {
	.speaker-pill > span {
		text-box-trim: trim-both;
		text-box-edge: cap alphabetic;
	}
}
</style>
